import { useMemo, useState } from 'react'
import { useGuildStore } from '@/store/guilds'
import { useAppStore } from '@/store/app'
import { describeError } from '@/lib/auth'
import { ALL, P, bits, has, type PermissionKey } from '@/lib/permissions'
import { ChannelType, type Channel, type Guild, type Overwrite } from '@/types/api'
import { cx } from '@/lib/cx'
import { Modal } from '../settings/Modal'
import { ConfirmModal } from './ConfirmModal'
import { Field } from '@/pages/AuthPage'
import { channelName } from '@/lib/channelName'
import { ChannelNameInput } from './ChannelNameInput'
import auth from '@/pages/AuthPage.module.css'
import layer from '../settings/SettingsLayer.module.css'
import styles from './ChannelSettingsModal.module.css'
import { Select } from '../Select'

const TEXT_PERMS: { key: PermissionKey; label: string }[] = [
  { key: 'VIEW_CHANNEL', label: 'Просматривать канал' },
  { key: 'SEND_MESSAGES', label: 'Отправлять сообщения' },
  { key: 'READ_MESSAGE_HISTORY', label: 'Читать историю' },
  { key: 'ATTACH_FILES', label: 'Прикреплять файлы' },
  { key: 'EMBED_LINKS', label: 'Встраивать ссылки' },
  { key: 'ADD_REACTIONS', label: 'Добавлять реакции' },
  { key: 'SEND_POLLS', label: 'Создавать опросы' },
  { key: 'SEND_VOICE_MESSAGES', label: 'Отправлять голосовые сообщения' },
  { key: 'MENTION_EVERYONE', label: 'Упоминать @everyone' },
  { key: 'MANAGE_MESSAGES', label: 'Управлять сообщениями' },
]
const VOICE_PERMS: { key: PermissionKey; label: string }[] = [
  ...TEXT_PERMS,
  { key: 'CONNECT', label: 'Подключаться' },
  { key: 'SPEAK', label: 'Говорить' },
  { key: 'STREAM', label: 'Демонстрировать экран' },
  { key: 'MUTE_MEMBERS', label: 'Отключать микрофон другим' },
  { key: 'MOVE_MEMBERS', label: 'Перемещать участников' },
]
const SLOWMODE = [0, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600]

function slowLabel(s: number): string {
  if (s === 0) return 'Выключен'
  if (s < 60) return `${s} с`
  if (s < 3600) return `${s / 60} мин`
  return `${s / 3600} ч`
}

type Tri = 'allow' | 'neutral' | 'deny'

/** Настройки канала: обзор (имя, тема, медленный режим) и права ролей в канале. */
export function ChannelSettingsModal({ guild, channel, perms, onClose }: { guild: Guild; channel: Channel; perms: bigint; onClose: () => void }) {
  const updateChannel = useGuildStore((s) => s.updateChannel)
  const deleteChannel = useGuildStore((s) => s.deleteChannel)
  const setView = useAppStore((s) => s.setView)
  const view = useAppStore((s) => s.view)
  const isCategory = channel.type === ChannelType.GuildCategory
  const isVoice = channel.type === ChannelType.GuildVoice
  const canRoles = has(perms, P.MANAGE_ROLES) && !isCategory
  const [tab, setTab] = useState<'overview' | 'permissions'>('overview')
  const [name, setName] = useState(channel.name ?? '')
  const isText = channel.type === ChannelType.GuildText || channel.type === ChannelType.GuildForum
  const preview = channelName(name, isText)
  const [topic, setTopic] = useState(channel.topic ?? '')
  const [slowmode, setSlowmode] = useState(channel.rate_limit_per_user ?? 0)
  const [nsfw, setNsfw] = useState(Boolean(channel.nsfw))
  const [userLimit, setUserLimit] = useState(channel.user_limit ?? 0)
  const [overwrites, setOverwrites] = useState<Overwrite[]>(channel.permission_overwrites ?? [])
  const [roleId, setRoleId] = useState(guild.id)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const roles = useMemo(() => [...guild.roles].sort((a, b) => b.position - a.position), [guild.roles])
  const permList = isVoice ? VOICE_PERMS : TEXT_PERMS
  const current = overwrites.find((o) => o.type === 0 && o.id === roleId)
  const state = (key: PermissionKey): Tri => {
    if (!current) return 'neutral'
    if (has(bits(current.allow), P[key])) return 'allow'
    if (has(bits(current.deny), P[key])) return 'deny'
    return 'neutral'
  }
  const setState = (key: PermissionKey, tri: Tri) => {
    setOverwrites((list) => {
      const ow = list.find((o) => o.type === 0 && o.id === roleId) ?? { id: roleId, type: 0 as const, allow: '0', deny: '0' }
      let allow = bits(ow.allow) & ~P[key]
      let deny = bits(ow.deny) & ~P[key]
      if (tri === 'allow') allow |= P[key]
      if (tri === 'deny') deny |= P[key]
      const next = { ...ow, allow: allow.toString(), deny: deny.toString() }
      const rest = list.filter((o) => !(o.type === 0 && o.id === roleId))
      return allow === 0n && deny === 0n ? rest : [...rest, next]
    })
  }
  const canGrant = (key: PermissionKey) => perms === ALL || has(perms, P[key])

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const patch: Parameters<typeof updateChannel>[1] = {}
      if (name.trim() !== (channel.name ?? '')) patch.name = name.trim()
      if (!isVoice && !isCategory && topic !== (channel.topic ?? '')) patch.topic = topic || null
      if (!isCategory && slowmode !== (channel.rate_limit_per_user ?? 0)) patch.rate_limit_per_user = slowmode
      if (!isVoice && !isCategory && nsfw !== Boolean(channel.nsfw)) patch.nsfw = nsfw
      if (isVoice && userLimit !== (channel.user_limit ?? 0)) patch.user_limit = userLimit
      if (canRoles && JSON.stringify(overwrites) !== JSON.stringify(channel.permission_overwrites ?? [])) patch.permission_overwrites = overwrites
      if (Object.keys(patch).length) await updateChannel(channel.id, patch)
      onClose()
    } catch (err) {
      const d = describeError(err)
      setError(d.fields.name ?? d.fields.topic ?? d.message)
      setBusy(false)
    }
  }

  if (deleting) {
    return (
      <ConfirmModal
        title={`Удалить ${isCategory ? 'категорию' : 'канал'} «${channel.name ?? ''}»`}
        text={isCategory ? 'Каналы категории останутся на сервере без категории.' : 'Все сообщения канала будут удалены. Это действие нельзя отменить.'}
        confirmLabel="Удалить"
        onCancel={() => setDeleting(false)}
        onConfirm={async () => {
          await deleteChannel(channel.id)
          if (view.kind === 'guild' && view.channelId === channel.id) setView({ kind: 'guild', guildId: guild.id, channelId: null })
          onClose()
        }}
      />
    )
  }

  return (
    <Modal title={`${isCategory ? 'Категория' : '#' + (channel.name ?? '')}`} onClose={onClose} width={560}>
      {canRoles ? (
        <nav className={styles.tabs} aria-label="Разделы">
          <button type="button" className={cx(styles.tab, tab === 'overview' && styles.tabActive)} onClick={() => setTab('overview')}>Обзор</button>
          <button type="button" className={cx(styles.tab, tab === 'permissions' && styles.tabActive)} onClick={() => setTab('permissions')}>Права доступа</button>
        </nav>
      ) : null}
      {tab === 'overview' ? (
        <div className={auth.form}>
          <Field label="Название" htmlFor="ch-name" error={error ?? undefined} hint={isText && preview && preview !== name.trim() ? `Будет называться #${preview}` : undefined}>
            <ChannelNameInput id="ch-name" value={name} onChange={setName} />
          </Field>
          {!isVoice && !isCategory ? (
            <>
              <Field label={channel.type === ChannelType.GuildForum ? "Описание форума" : "Тема канала"} htmlFor="ch-topic" hint={channel.type === ChannelType.GuildForum ? "О чём обсуждать в этом форуме" : "Показывается в шапке канала"}>
                <textarea id="ch-topic" className={cx(auth.input, styles.textarea)} value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={1024} rows={3} placeholder="О чём этот канал" />
              </Field>
              <Field label="Медленный режим" htmlFor="ch-slow" hint="Пауза между сообщениями одного участника; на модераторов не действует">
                <Select id="ch-slow" value={slowmode} options={SLOWMODE.map((s) => ({ value: s, label: slowLabel(s) }))} onChange={setSlowmode} />
              </Field>
              <label className={auth.checkbox}>
                <input type="checkbox" checked={nsfw} onChange={(e) => setNsfw(e.target.checked)} /> Канал 18+ (NSFW): участники подтверждают возраст, младше 18 лет канал недоступен
              </label>
            </>
          ) : null}
          {isVoice ? (
            <>
            <Field label="Медленный режим чата" htmlFor="ch-slow" hint="Пауза между сообщениями одного участника; на модераторов не действует">
              <Select id="ch-slow" value={slowmode} options={SLOWMODE.map((s) => ({ value: s, label: slowLabel(s) }))} onChange={setSlowmode} />
            </Field>
            <Field label="Лимит участников" htmlFor="ch-limit" hint="0 — без ограничения">
              <input id="ch-limit" className={auth.input} type="number" min={0} max={99} value={userLimit} onChange={(e) => setUserLimit(Number(e.target.value))} />
            </Field>
            </>
          ) : null}
        </div>
      ) : (
        <div className={styles.perms}>
          <ul className={styles.roleList} aria-label="Роли">
            {roles.map((r) => (
              <li key={r.id}>
                <button type="button" className={cx(styles.role, roleId === r.id && styles.roleActive)} onClick={() => setRoleId(r.id)}>
                  <span className={styles.roleDot} style={{ background: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : 'var(--text-muted)' }} />
                  {r.name}
                  {overwrites.some((o) => o.type === 0 && o.id === r.id) ? <span className={styles.roleMark} data-tooltip="Есть перекрытия" /> : null}
                </button>
              </li>
            ))}
          </ul>
          <div className={styles.grid}>
            {permList.map((p) => {
              const s = state(p.key)
              const disabled = !canGrant(p.key)
              return (
                <div key={p.key} className={styles.permRow}>
                  <span className={styles.permLabel}>{p.label}</span>
                  <div className={styles.tri} role="radiogroup" aria-label={p.label}>
                    <button type="button" role="radio" aria-checked={s === 'deny'} className={cx(styles.triButton, styles.triDeny, s === 'deny' && styles.triOn)} disabled={disabled} onClick={() => setState(p.key, 'deny')} data-tooltip="Запретить" aria-label="Запретить">✕</button>
                    <button type="button" role="radio" aria-checked={s === 'neutral'} className={cx(styles.triButton, s === 'neutral' && styles.triOn)} disabled={disabled} onClick={() => setState(p.key, 'neutral')} data-tooltip="Как у роли" aria-label="Как у роли">/</button>
                    <button type="button" role="radio" aria-checked={s === 'allow'} className={cx(styles.triButton, styles.triAllow, s === 'allow' && styles.triOn)} disabled={disabled} onClick={() => setState(p.key, 'allow')} data-tooltip="Разрешить" aria-label="Разрешить">✓</button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
      {error && tab === 'permissions' ? <p className={auth.error} role="alert">{error}</p> : null}
      <div className={styles.footer}>
        <button type="button" className={cx(layer.button, layer.buttonDanger)} onClick={() => setDeleting(true)}>Удалить {isCategory ? 'категорию' : 'канал'}</button>
        <div className={styles.footerRight}>
          <button type="button" className={styles.cancel} onClick={onClose}>Отмена</button>
          <button type="button" className={layer.button} disabled={busy || !name.trim()} onClick={() => void save()}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
        </div>
      </div>
    </Modal>
  )
}
